import React, { useState, useEffect } from 'react';
import Joyride, { EVENTS, STATUS } from 'react-joyride';

/* Homepage tour targets. Every selector below MUST resolve to an element
   that's mounted on the homepage route by default (no drawer open, no
   match). Previous iterations of this array still referenced pre-redesign
   selectors like `.login-card` and `.active-players-counter` which no
   longer exist, and Joyride would deadlock on step 0 because the callback
   can't advance past a `TARGET_NOT_FOUND` step. */
const tutorialSteps = [
  {
    target: 'body',
    content: 'Welcome to Movie Mayhem — rated 1v1, turn-based 4v4 battles. Each match rolls four random fighters per player (four moves each); outplay and climb the ladder.',
    placement: 'center',
    title: 'Welcome to Movie Mayhem',
    disableBeacon: true,
  },
  {
    target: '.find-match-button',
    content: 'This is the queue. Click here to get paired — both of you load a fresh random 4-fighter roster when the match starts.',
    placement: 'top',
    title: 'Enter the Arena',
  },
  {
    target: '.players-online',
    content: 'Live count of players currently online. The higher it climbs, the faster you\'ll find a match.',
    placement: 'top',
    title: 'Players Online',
  },
  {
    target: '[data-tour="characters-button"]',
    content: 'Browse every fighter — stats, per-move use limits from the database, damage vs status moves, and what each effect does.',
    placement: 'bottom',
    title: 'Fighter roster',
  },
  {
    target: '[data-tour="leaderboard-button"]',
    content: 'Global rankings by ELO rating. Track wins, losses, and your climb up the ladder.',
    placement: 'bottom',
    title: 'Box Office',
  },
];

const battleTutorialSteps = [
  {
    target: '.character-selection',
    content: 'Your four-fighter bench this match is random — same rules for both players. Each fighter gets four sampled moves; switch when you need a fresh body in the fight.',
    placement: 'center',
    title: 'Your roster'
  },
  {
    target: '.battle-stats',
    content: 'Monitor your character\'s Health, Attack, Defense, and Speed. Speed decides who strikes first when both players attack.',
    placement: 'right',
    title: 'Battle stats'
  },
  {
    target: '.moves-panel',
    content: 'Pick from four moves per active fighter. Some deal damage, others buff or debuff — each move has limited uses per match (shown as Uses on the buttons).',
    placement: 'left',
    title: 'Moves'
  },
  {
    target: '.switch-character-button',
    content: 'Switch to another fighter on your bench when it buys you an advantage — voluntary switches use your turn and resolve before attacks.',
    placement: 'top',
    title: 'Switching'
  },
  {
    target: '.battle-log',
    content: 'Follow the fight here: turns, damage, misses, and effects land in order as they resolve.',
    placement: 'left',
    title: 'Battle log'
  }
];

const Tutorial = ({ isOpen, onClose, tutorialType = 'main' }) => {
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  /* Every time the parent opens the tour, start it fresh. Previously this
     effect gated on a `movie-mayhem-tutorial-${tutorialType}-seen`
     localStorage flag and silently called onClose() for anyone who'd
     completed it once before, which made the "How to Play" buttons look
     broken on any return visit. First-visit auto-open is already handled
     by the parent (App.js / MyForm), so Tutorial itself should just trust
     the `isOpen` prop and run. */
  useEffect(() => {
    if (isOpen) {
      setStepIndex(0);
      setRun(true);
    } else {
      setRun(false);
    }
  }, [isOpen, tutorialType]);

  const handleJoyrideCallback = (data) => {
    const { status, type, action, index } = data;
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];

    if (finishedStatuses.includes(status)) {
      setRun(false);
      /* Shared flag with App.js auto-run + MyForm first-visit gate so all
         three agree on "user has seen the homepage Joyride". Manual opens
         via the Help FAB still work because TutorialOverlay itself only
         reads from the `isOpen` prop. */
      localStorage.setItem('movie-mayhem-tutorial-seen', 'true');
      onClose();
      return;
    }

    /* Advance on normal STEP_AFTER, AND on TARGET_NOT_FOUND. The previous
       handler treated a missing target as "stay at this index", which
       silently deadlocked the whole tour on any stale selector. We always
       move forward by 1 when Joyride tells us a step is done — direction
       still respects the user's Back/Next choice via `action`. */
    if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
      const next = index + (action === 'prev' ? -1 : 1);
      const steps = tutorialType === 'battle' ? battleTutorialSteps : tutorialSteps;

      if (next < 0 || next >= steps.length) {
        // Ran off the end of the tour — close cleanly so the user can
        // re-open it later instead of sitting on a phantom step.
        setRun(false);
        localStorage.setItem('movie-mayhem-tutorial-seen', 'true');
        onClose();
        return;
      }

      setStepIndex(next);
    }
  };

  const steps = tutorialType === 'battle' ? battleTutorialSteps : tutorialSteps;

  if (!isOpen) return null;

  return (
    <Joyride
      steps={steps}
      run={run}
      stepIndex={stepIndex}
      callback={handleJoyrideCallback}
      continuous={true}
      showProgress={true}
      showSkipButton={true}
      scrollToFirstStep={true}
      scrollOffset={100}
      /* Joyride takes raw CSS values (no CSS var resolution), so we inline
         the mass-poster palette hex codes here. Kept in sync with tokens.js
         by convention — if that ever drifts, the tutorial will stand out. */
      styles={{
        options: {
          primaryColor: '#FFB13C',          // amber-500
          backgroundColor: '#0E0C10',       // ink-900
          textColor: '#F6F1E4',             // cream-100
          overlayColor: 'rgba(8, 7, 10, 0.88)',
          spotlightShadow: '0 0 0 2px #FFB13C, 0 0 24px rgba(255, 177, 60, 0.45)',
          beaconSize: 36,
          zIndex: 10000,
        },
        tooltip: {
          borderRadius: 0,
          padding: '22px 24px',
          fontSize: 14,
          fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          boxShadow: '0 8px 28px rgba(8, 7, 10, 0.55)',
          background: '#0E0C10',                 // ink-900
          border: '1px solid #22202A',           // ink-700
          color: '#F6F1E4',                      // cream-100
          clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 14px), calc(100% - 14px) 100%, 0 100%)',
        },
        tooltipTitle: {
          fontFamily: "'Oswald', 'Anton', Impact, sans-serif",
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          marginBottom: 10,
          color: '#FFB13C',                      // amber-500
        },
        tooltipContent: {
          lineHeight: 1.55,
          marginBottom: 12,
          color: '#E8DFC8',                      // cream-200
        },
        buttonNext: {
          backgroundColor: '#FFB13C',            // amber-500
          border: 'none',
          borderRadius: 0,
          padding: '9px 18px',
          fontFamily: "'Oswald', 'Anton', Impact, sans-serif",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#08070A',                      // ink-950
          cursor: 'pointer',
          clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)',
        },
        buttonBack: {
          backgroundColor: '#16131A',            // ink-800
          border: '1px solid #22202A',
          borderRadius: 0,
          padding: '9px 18px',
          fontFamily: "'Oswald', 'Anton', Impact, sans-serif",
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: '#E8DFC8',
          cursor: 'pointer',
          marginRight: 8,
          clipPath: 'polygon(0 0, 100% 0, 100% calc(100% - 8px), calc(100% - 8px) 100%, 0 100%)',
        },
        buttonSkip: {
          backgroundColor: 'transparent',
          border: 'none',
          padding: '8px 12px',
          color: '#BFB3A0',                      // cream-300
          fontFamily: "'Inter', sans-serif",
          fontSize: 11,
          letterSpacing: '0.12em',
          textTransform: 'uppercase',
          cursor: 'pointer',
        },
        beacon: {
          backgroundColor: '#FFB13C',            // amber-500
          border: '2px solid #08070A',
          borderRadius: '50%',
          boxShadow: '0 0 0 6px rgba(255, 177, 60, 0.3)',
          animation: 'joyride-beacon 1.2s infinite ease-in-out',
        },
        spotlight: {
          borderRadius: 0,
          border: '1px solid #FFB13C',
          boxShadow: '0 0 0 9999px rgba(8, 7, 10, 0.88), 0 0 24px rgba(255, 177, 60, 0.45)',
        }
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Enter the Arena',
        next: 'Next',
        skip: 'Skip',
      }}
      floaterProps={{
        disableAnimation: false,
        styles: {
          arrow: {
            length: 8,
            spread: 16,
          },
        },
      }}
    />
  );
};

export default Tutorial;
